// Editor column. Title field on top, the markdown body below — toggle
// between editing (CodeMirror) and a live rendered preview. When the AI
// is mid-<edit>, the editor goes read-only so an incoming replacement
// doesn't fight the cursor.

import { useMemo, useState } from "react";
import { marked } from "marked";
import { useComposeStore } from "../../store/composeStore";
import { DocCodeMirror } from "../DocCodeMirror";

export function EditorPane() {
  const activeId = useComposeStore((s) => s.activeId);
  const drafts = useComposeStore((s) => s.drafts);
  const aiEditing = useComposeStore((s) => s.aiEditing);
  const setTitle = useComposeStore((s) => s.setTitle);
  const setBody = useComposeStore((s) => s.setBody);
  const [preview, setPreview] = useState(false);

  const draft = drafts.find((d) => d.id === activeId);
  const html = useMemo(
    () => (draft ? (marked.parse(draft.body) as string) : ""),
    [draft?.body, draft?.id],
  );

  if (!draft) return <div className="ed-pane" />;

  return (
    <div className="ed-pane">
      <div className="ed-top">
        <input
          className="ed-title"
          placeholder="Untitled"
          value={draft.title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <button className={"ed-toggle" + (preview ? " ed-toggle-on" : "")} onClick={() => setPreview((p) => !p)}>
          {preview ? "edit" : "preview"}
        </button>
      </div>

      <div className="ed-body">
        {preview
          ? <div className="ed-preview" dangerouslySetInnerHTML={{ __html: html }} />
          : <DocCodeMirror docId={draft.id} value={draft.body} onChange={setBody} readOnly={aiEditing} />}
      </div>

      <style>{`
        .ed-pane { display: flex; flex-direction: column; height: 100%; min-height: 0; background: var(--paper); }
        .ed-top { flex: none; display: flex; align-items: center; gap: var(--space-3); padding: var(--space-3) var(--space-5); border-bottom: 1px solid var(--line); }
        .ed-title {
          flex: 1; border: none; background: transparent;
          font-family: var(--font-serif); font-size: 26px; font-weight: 600; color: var(--ink);
        }
        .ed-title:focus { outline: none; }
        .ed-title::placeholder { color: var(--ink-faint); }
        .ed-toggle {
          flex: none; font-family: var(--font-mono); font-size: 11px;
          color: var(--ink-soft); background: var(--paper-sunk);
          border: 1px solid var(--line); border-radius: 999px; padding: 4px 12px; cursor: pointer;
        }
        .ed-toggle-on, .ed-toggle:hover { color: var(--teal); border-color: var(--teal); }
        .ed-body { flex: 1; min-height: 0; }
        .ed-preview {
          height: 100%; overflow: auto; padding: var(--space-5);
          max-width: 720px; margin: 0 auto;
          font-family: var(--font-serif); font-size: 17px; line-height: 1.7; color: var(--ink);
        }
        .ed-preview h1, .ed-preview h2, .ed-preview h3 { line-height: 1.25; }
        .ed-preview pre { background: var(--ink); color: var(--paper); padding: 14px; border-radius: 8px; overflow: auto; font-size: 14px; }
        .ed-preview code { font-family: var(--font-mono); font-size: 0.9em; }
        .ed-preview blockquote { border-left: 3px solid var(--teal); margin: 0; padding-left: 14px; color: var(--ink-soft); }
        .ed-preview img { max-width: 100%; height: auto; border-radius: 8px; }
      `}</style>
    </div>
  );
}
